import { Stack, StackProps, Tooltip, Typography, TypographyProps } from '@mui/material'
import isNil from 'lodash/isNil'
import { ReactNode, type ImgHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { InitialsAvatar } from '@omnea-app/fe-users-and-groups/avatar/InitialsAvatar'

export interface Props extends StackProps {
  text: string
  imgUrl?: string | null
  imgProps?: ImgHTMLAttributes<HTMLImageElement>
  /** Renders in place of the initials avatar — e.g. an integration or Omnea AI avatar. */
  avatar?: ReactNode
  avatarVariant?: 'square' | 'rounded' | 'circular'
  tooltipLabel?: string
  disableTooltip?: boolean
  userId: string | null
  textProps?: TypographyProps
  isArchived?: boolean
}

export const ProfileBox = ({
  text,
  imgUrl,
  imgProps,
  avatar,
  tooltipLabel,
  avatarVariant = 'rounded',
  disableTooltip = false,
  userId,
  textProps,
  isArchived,
  ...stackProps
}: Props) => {
  const { user: loggedInUser } = useAuthenticatedUser()
  const { t: tCommon } = useTranslation('common')
  const isLoggedInUser = loggedInUser?.id === userId
  const isUserAvatar = !isNil(userId)
  // Default grey for other users; passed through InitialsAvatar's own sx so the
  // email/id hash (applied inside the leaf) wins over it when the flag is on.
  const otherUserBackground = !isLoggedInUser && isUserAvatar ? { backgroundColor: ' grey' } : {}

  const displayText = isArchived ? `${text} ${tCommon('archived')}` : text

  return (
    <Tooltip title={!disableTooltip ? tooltipLabel || text : ''}>
      <Stack
        direction="row"
        spacing={2}
        {...stackProps}
        sx={{ alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis', ...stackProps.sx }}
      >
        {avatar ?? (
          <InitialsAvatar
            alt={text}
            src={imgUrl ? imgUrl.replace('http://', 'https://') : undefined}
            slotProps={{ img: imgProps }}
            variant={avatarVariant}
            fullName={text}
            colourSubjectId={userId}
            isArchived={isArchived}
            sx={{ lineHeight: '25px', '& .MuiAvatar-img': { objectFit: 'contain' }, ...otherUserBackground }}
          />
        )}
        <Typography variant="body-regular" noWrap {...textProps} {...(isArchived && { color: 'error.dark' })}>
          {displayText}
        </Typography>
      </Stack>
    </Tooltip>
  )
}
